// React
import { memo, useCallback, useMemo, useState } from "react";

// Servisni sloj
import { getExpenseTypesAPI } from "services/api/finance/expense-types/api";
import { ExpenseType } from "services/api/finance/expense-types/types";
import { buildDropdownCacheKey, DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// UI
import { DropdownAutocomplete } from "ui/components/Dropdown/DropdownAutocomplete";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

import { EXPENSE_TYPES_CACHE_TTL } from "./consts";
import { ExpenseTypeDropdownProps } from "./types";

/**
 * Vrsta troška iz šifrarnika. Bira se podgrupa („Knjigovodstvo") jer se po njoj troškovi
 * zbrajaju, a nadgrupa („Uprava") stoji kao zaglavlje popisa — nadgrupa koja ima podgrupe nije
 * izbor nego naslov.
 */
const ExpenseTypeDropdown: React.FC<ExpenseTypeDropdownProps> = (props) => {
  const {
    name,
    value,
    onChange,
    label,
    placeholder = "Vrsta troška",
    error,
    disabled,
    required,
    appliesTo,
    cssClassName,
  } = props;

  const [search, setSearch] = useState<string>("");

  const { data } = useDropdownCache(
    buildDropdownCacheKey(DropdownCacheKey.ExpenseTypes, appliesTo),
    () => getExpenseTypesAPI(appliesTo),
    EXPENSE_TYPES_CACHE_TTL
  );

  const categories: ExpenseType[] = useMemo(() => data ?? [], [data]);

  const options: DropdownOption[] = useMemo(() => {
    const parents = categories.filter(category => category.parentId === null);
    const childrenByParent = new Map<string, ExpenseType[]>();

    categories
      .filter(category => category.parentId !== null)
      .forEach(category => {
        const siblings = childrenByParent.get(category.parentId!) ?? [];
        childrenByParent.set(category.parentId!, [...siblings, category]);
      });

    const byOrdinal = (a: ExpenseType, b: ExpenseType) =>
      a.ordinal - b.ordinal || a.name.localeCompare(b.name);

    const sortedParents = [...parents].sort(byOrdinal);

    // Zaglavlje se crta samo kad se `group` promijeni u odnosu na prethodni redak, pa podgrupe
    // moraju biti složene jedna do druge ispod svoje nadgrupe.
    const grouped: DropdownOption[] = [];

    // Nadgrupa bez podgrupa je i sama izbor — ide na vrh, bez zaglavlja.
    sortedParents
      .filter(parent => (childrenByParent.get(parent.id) ?? []).length === 0)
      .forEach(parent => grouped.push({ value: parent.id, text: parent.name }));

    sortedParents.forEach(parent => {
      const children = childrenByParent.get(parent.id) ?? [];

      [...children].sort(byOrdinal).forEach(child => {
        grouped.push({ value: child.id, text: child.name, group: parent.name });
      });
    });

    const term = search.trim().toLowerCase();
    if (!term) return grouped;

    const matches = grouped.filter(option =>
      String(option.text).toLowerCase().includes(term)
      || (option.group ?? "").toLowerCase().includes(term)
      || (categories.find(category => category.id === option.value)?.code ?? "").toLowerCase().includes(term)
    );

    // Odabrana grupa ostaje u popisu i kad je pretraga izbaci — inače polje padne na placeholder.
    const selected = grouped.find(option => option.value === value);
    if (selected && !matches.some(option => option.value === value)) {
      return [selected, ...matches];
    }

    return matches;
  }, [categories, search, value]);

  const handleSearchChange = useCallback((term: string) => {
    setSearch(term);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setSearch("");
  }, []);

  return (
    <DropdownAutocomplete
      name={name}
      value={value}
      options={options}
      onChange={onChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      label={label}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      required={required}
      inputClassName={cssClassName}
    />
  );
};

export default memo(ExpenseTypeDropdown);
